import api from './api';

const tradeService = {
  /**
   * Get trades with filtering and pagination
   */
  async getTrades(params = {}) {
    const response = await api.get('/trades', { params });
    return response.data;
  },

  /**
   * Get open/partial trades (for linking exits)
   */
  /**
   * @param {string|null} product - filter to one product (exit matching)
   * @param {object} [opts]
   * @param {boolean} [opts.rollupStrategies] - collapse multi-leg option
   *   strategy legs into ONE row each (risk = strategy defined risk, one
   *   position). Callers that count positions or sum risk want this; the
   *   reconciliation exit-matcher does NOT (it matches individual legs).
   */
  async getOpenTrades(product = null, opts = {}) {
    const params = product ? { product } : {};
    if (opts.rollupStrategies) params.rollup = 'strategies';
    const response = await api.get('/trades/open', { params });
    return response.data;
  },

  /**
   * Get a single trade with linked transactions
   */
  async getTrade(id) {
    const response = await api.get(`/trades/${id}`);
    return response.data;
  },

  /**
   * Create a trade from a single transaction
   */
  async createFromTransaction(transactionId, options = {}) {
    const response = await api.post('/trades/create-from-transaction', {
      transactionId,
      ...options
    });
    return response.data;
  },

  /**
   * Merge multiple transactions into a single trade
   */
  async mergeTransactions(transactionIds, options = {}) {
    const response = await api.post('/trades/merge-transactions', {
      transactionIds,
      ...options
    });
    return response.data;
  },

  /**
   * Link an exit transaction to a trade
   */
  async linkExit(tradeId, transactionId) {
    const response = await api.post(`/trades/${tradeId}/link-exit`, { transactionId });
    return response.data;
  },

  /**
   * Unlink a transaction from a trade
   */
  async unlinkTransaction(tradeId, transactionId) {
    const response = await api.delete(`/trades/${tradeId}/unlink/${transactionId}`);
    return response.data;
  },

  /**
   * Update trade details
   */
  async updateTrade(id, data) {
    const response = await api.put(`/trades/${id}`, data);
    return response.data;
  },

  /**
   * Get pending corporate actions detected from IB Review transactions
   */
  async getPendingCorporateActions() {
    const response = await api.get('/trades/pending-corporate-actions');
    return response.data;
  },

  /** Shares that went out for cash: record the payment as their sale (16 Oct 2026). */
  async recordTakeover(eventKey, paymentId) {
    const response = await api.post('/trades/corporate-actions/record-takeover', { eventKey, paymentId });
    return response.data;
  },

  /** A split found on Yahoo: apply it, or dismiss it as not this holding (16 Oct 2026). */
  async applyCorporateActionEvent(eventKey) {
    const response = await api.post('/trades/corporate-actions/apply-event', { eventKey });
    return response.data;
  },

  async dismissCorporateActionEvent(eventKey) {
    const response = await api.post('/trades/corporate-actions/dismiss-event', { eventKey });
    return response.data;
  },

  /** Shares out, their money paid to the bank: the sale and the money leaving, same day (16 Oct 2026). */
  async recordPaidOutside(eventKey, amount) {
    const response = await api.post('/trades/corporate-actions/record-paid-outside', { eventKey, amount });
    return response.data;
  },

  /**
   * Apply a corporate action (merger, split, symbol_change) to a trade
   */
  async applyCorporateAction(tradeId, data) {
    const response = await api.put(`/trades/${tradeId}/corporate-action`, data);
    return response.data;
  },

  /**
   * Undo a corporate action on a trade
   * @param {string} tradeId
   * @param {number} caIndex - Index into corporateActions array (-1 for last)
   */
  /**
   * Batch apply a corporate action to multiple trades
   */
  async applyCorporateActionBatch(tradeIds, actionPayload) {
    const response = await api.post('/trades/corporate-action-batch', { tradeIds, ...actionPayload });
    return response.data;
  },

  async undoCorporateAction(tradeId, caIndex = -1) {
    const response = await api.post(`/trades/${tradeId}/undo-corporate-action`, { caIndex });
    return response.data;
  },

  /**
   * Delete a trade
   */
  async deleteTrade(id) {
    const response = await api.delete(`/trades/${id}`);
    return response.data;
  },

  /**
   * Get suggested trades for an exit transaction
   */
  async getSuggestedTrades(transactionId) {
    const response = await api.get(`/trades/suggest/${transactionId}`);
    return response.data;
  },

  /**
   * Get trades grouped by trade_group with aggregated statistics
   */
  async getGroupedTrades(params = {}) {
    const response = await api.get('/trades/grouped', { params });
    return response.data;
  },

  /**
   * Get win rate treating trade groups as single trades
   */
  async getGroupedWinRate() {
    const response = await api.get('/trades/grouped/win-rate');
    return response.data;
  },

  /**
   * Get all trade groups for dropdown selection
   */
  async getTradeGroups() {
    const response = await api.get('/trades/groups');
    return response.data;
  },

  /**
   * Get open trade groups for a specific product (for smart suggestions)
   */
  /** Lot sizes Kodahfy would use per product (user history → verified table → 1). */
  async getLotSizes(products) {
    const response = await api.post('/trades/lot-sizes', { products });
    return response.data?.data?.lotSizes || {};
  },

  async getOpenGroupsForProduct(product) {
    const response = await api.get('/trades/groups/for-product', {
      params: { product }
    });
    return response.data;
  },

  /**
   * Get open trades for split-view matching interface
   * Returns trades grouped by product with entry details
   */
  async getOpenTradesForMatching() {
    const response = await api.get('/trades/open-for-matching');
    return response.data;
  },

  /**
   * Link an exit transaction to a trade with specified exit method
   * @param {string} tradeId - The trade ID to link to
   * @param {string} transactionId - The close transaction ID
   * @param {string} exitMethod - 'average', 'fifo', 'lifo', or 'manual'
   * @param {Object} exitMetadata - Optional exit metadata (reason, notes, tags, images)
   */
  async linkExitWithMethod(tradeId, transactionId, exitMethod = 'average', exitMetadata = null) {
    const response = await api.post(`/trades/${tradeId}/link-exit-with-method`, {
      transactionId,
      exitMethod,
      exitMetadata
    });
    return response.data;
  },

  /**
   * Get entry count for a trade or its trade group
   * Used to determine if exit method modal should be shown
   */
  async getTradeEntryCount(tradeId) {
    const response = await api.get(`/trades/${tradeId}/entry-count`);
    return response.data;
  },

  /**
   * Get entry details for a trade (system, setup, notes, tags, images)
   * Used to display entry context during exit matching
   */
  async getTradeEntryDetails(tradeId) {
    const response = await api.get(`/trades/${tradeId}/entry-details`);
    return response.data;
  },

  /**
   * Get ALL unmatched close transactions for inline matching
   * @param {boolean} groupByProduct - Whether to group closes by product
   * @returns {Object} All closes with available trades and suggestions
   */
  async getAllUnmatchedCloses(groupByProduct = false, scope) {
    // Item 85 — scope 'options' serves the Options recon tab's close list
    const response = await api.get('/trades/closes/unmatched', {
      params: scope ? { groupByProduct, scope } : { groupByProduct }
    });
    return response.data;
  },

  // Item 85 Phase 2 — options strategy grouping
  async getOptionsProposals() {
    const response = await api.get('/trades/options/proposals');
    return response.data;
  },

  async confirmOptionsStrategy(payload) {
    // { txnIds, trading_system, setup, systemId, group_source }
    const response = await api.post('/trades/options/strategies', payload);
    return response.data;
  },

  // legs may be a single tradeId or an array of candidate legs the fill spans
  // (a reused strike closes several spreads at once → the fill splits per-lot).
  async closeOptionsLeg(txnId, legs) {
    const body = Array.isArray(legs) ? { txnId, tradeIds: legs } : { txnId, tradeId: legs };
    const response = await api.post('/trades/options/close', body);
    return response.data;
  },

  /**
   * Batch match multiple closes to their suggested trades
   * @param {Array} closeIds - Array of close transaction IDs to match
   * @param {string} exitMethod - Exit method to use (average, fifo, lifo)
   * @returns {Object} Results with matched, failed, and skipped counts
   */
  async batchMatchCloses(closeIds, exitMethod = 'average') {
    const response = await api.post('/trades/closes/batch-match', {
      closeIds,
      exitMethod
    });
    return response.data;
  },

  /**
   * Get all matched (linked) exit transactions for Remove & Redo view
   */
  async getMatchedExits() {
    const response = await api.get('/trades/exits/matched');
    return response.data;
  },

  /**
   * Unlink an exit transaction from its trade (Remove & Redo)
   */
  async unlinkExit(transactionId) {
    const response = await api.put(`/trades/exits/${transactionId}/unlink`);
    return response.data;
  },

  /**
   * Bulk unlink exit transactions (Remove & Redo multiple)
   */
  async bulkUnlinkExits(transactionIds) {
    const response = await api.post('/trades/exits/bulk-unlink', { transactionIds });
    return response.data;
  },

  /**
   * Waterfall exit — one exit transaction closes multiple trades in FIFO order
   * @param {string} exitTransactionId - The close transaction ID
   * @param {string[]} tradeIds - Array of trade IDs to close against
   * @param {string} exitMethod - 'average', 'fifo', 'lifo'
   * @param {Object|null} exitMetadata - exit notes, tags, reason
   */
  async waterfallExit(exitTransactionId, tradeIds, exitMethod = 'fifo', exitMetadata = null, manualAllocations = null) {
    const response = await api.post('/trades/waterfall-exit', {
      exitTransactionId,
      tradeIds,
      exitMethod,
      exitMetadata,
      manualAllocations
    });
    return response.data;
  },

  /**
   * Unlink a waterfall exit — removes all links for an exit spread across multiple trades
   */
  async unlinkWaterfallExit(transactionId) {
    const response = await api.put(`/trades/exits/${transactionId}/unlink-waterfall`);
    return response.data;
  },

  /**
   * Get trading systems
   */
  async getSystems() {
    const response = await api.get('/systems');
    return response.data;
  },

  /**
   * Create a trading system
   */
  async createSystem(data) {
    const response = await api.post('/systems', data);
    return response.data;
  },

  /**
   * Get all notes for a trade (entry, live, exit)
   * @param {string} tradeId - The trade ID
   * @returns {Object} Complete notes context with entry_notes, live_notes, exit_notes, images, etc.
   */
  /**
   * Save any of entry / live / exit notes (and image strips) in one call.
   * PATCH /trades/:id/notes — entry and exit replace, live_note appends.
   */
  async updateTradeNotes(tradeId, data) {
    const response = await api.patch(`/trades/${tradeId}/notes`, data);
    return response.data;
  },

  async getTradeNotes(tradeId) {
    const response = await api.get(`/trades/${tradeId}/notes`);
    return response.data;
  },

  /**
   * Update live notes for an open trade (append-only)
   * @param {string} tradeId - The trade ID
   * @param {string} liveNote - New note to append (will be timestamped)
   * @param {Array} liveImages - Optional images to add
   * @returns {Object} Updated live notes
   */
  async updateLiveNotes(tradeId, liveNote, liveImages = []) {
    const response = await api.patch(`/trades/${tradeId}/live-notes`, {
      live_note: liveNote,
      live_images: liveImages
    });
    return response.data;
  },

  /**
   * Update trailing stop for a trade
   * @param {string} tradeId - The trade ID
   * @param {number} trailingStop - New trailing stop price
   * @param {number} currentPrice - Optional current price for live risk calculation
   * @returns {Object} Updated trade with live_risk recalculated
   */
  async updateTrailingStop(tradeId, trailingStop, currentPrice = null) {
    const response = await api.patch(`/trades/${tradeId}/trailing-stop`, {
      trailing_stop: trailingStop,
      current_price: currentPrice
    });
    return response.data;
  },

  /**
   * Get capital deployment over time by trading system
   * Shows % of FUM deployed per month, broken down by system
   * @param {Object} options - { reportingCurrency }
   * @returns {Object} { months, systems, deploymentData, summary }
   */
  async getCapitalDeployment(options = {}) {
    const response = await api.get('/trades/capital-deployment', { params: options });
    return response.data;
  }
};

export default tradeService;
